import React, { useEffect, useMemo, useState } from "react";
import SEO from "@/components/SEO";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, Search, Image as ImageIcon, X, Star, ShoppingBag, Check, Lock, BadgeCheck } from "lucide-react";
import { customerApi, fmtErr } from "@/customer/api";
import { useCustomerAuth } from "@/customer/AuthContext";
import { useCart } from "@/customer/CartContext";

export default function EventGallery() {
  const { slug, sid } = useParams();
  const navigate = useNavigate();
  const { user } = useCustomerAuth();
  const { addItems, refresh: refreshCart, count: cartCount } = useCart();

  const [data, setData] = useState({ event: null, subfolder: null, photos: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    customerApi.get(`/public/events/${slug}/folders/${sid}`)
      .then((r) => { if (active) setData(r.data); })
      .catch((e) => toast.error(fmtErr(e)))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [slug, sid]);

  const list = useMemo(() => (data.photos || []).filter((p) =>
    (filter === "all" || (filter === "featured" && p.is_featured)) &&
    (!q || (p.display_name || "").toLowerCase().includes(q.toLowerCase()))
  ), [data.photos, q, filter]);

  const toggleSelect = (p) => {
    if (p.purchased) {
      toast.message("You already purchased this photo.");
      return;
    }
    if (p.in_cart) {
      toast.message("Already in your cart.");
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(p.id)) next.delete(p.id);
      else next.add(p.id);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const handleAddToCart = async () => {
    if (!user) {
      toast.message("Please log in to add photos to your cart.");
      navigate("/auth");
      return;
    }
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setAdding(true);
    try {
      const r = await addItems(ids);
      const parts = [];
      if (r.added) parts.push(`${r.added} added`);
      if (r.already_in_cart) parts.push(`${r.already_in_cart} already in cart`);
      if (r.already_purchased) parts.push(`${r.already_purchased} already purchased`);
      toast.success(parts.join(" · ") || "Cart updated");
      clearSelection();
      // Refetch gallery to update badges
      const fresh = await customerApi.get(`/public/events/${slug}/folders/${sid}`);
      setData(fresh.data);
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setAdding(false); }
  };

  const addOne = async (p) => {
    if (!user) {
      toast.message("Please log in to add photos to your cart.");
      navigate("/auth");
      return;
    }
    if (p.purchased) { toast.message("You already purchased this photo."); return; }
    if (p.in_cart) { toast.message("Already in your cart."); return; }
    try {
      await addItems([p.id]);
      toast.success(`${p.display_name || "Photo"} added to cart`);
      const fresh = await customerApi.get(`/public/events/${slug}/folders/${sid}`);
      setData(fresh.data);
      setOpen(null);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="page-event-gallery" className="pt-32 pb-32">
      <SEO title="Photo Gallery — WelkomBiz Photography" description="Watermarked photo previews from WelkomBiz Photography. Select and add to cart to unlock full-quality downloads." />
      <section className="max-w-[1280px] mx-auto px-5 sm:px-8 space-y-3">
        <nav className="text-[10px] tracking-luxe uppercase text-white/45 flex flex-wrap items-center gap-2">
          <Link to="/events" className="hover:text-white">Events</Link>
          <span>›</span>
          <Link to={`/events/${slug}`} className="hover:text-white truncate max-w-[180px]">{data.event?.title || "Event"}</Link>
          <span>›</span>
          <span className="text-white">{data.subfolder?.name || "Folder"}</span>
        </nav>
        <Link to={`/events/${slug}`} data-testid="gallery-back" className="inline-flex items-center gap-2 text-[10px] tracking-luxe uppercase text-white/55 hover:text-white">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Event
        </Link>
        <h1 className="font-display text-4xl sm:text-5xl"><span className="text-chrome">{data.subfolder?.name || "Gallery"}</span></h1>
        <p className="text-[11px] tracking-luxe uppercase text-white/45">{data.event?.title} · {data.photos?.length || 0} photo{(data.photos?.length || 0) === 1 ? "" : "s"}</p>
      </section>

      <section className="max-w-[1280px] mx-auto px-5 sm:px-8 mt-6">
        <div className="glass chrome-border rounded-md p-4 sm:p-5 flex items-start gap-3">
          <ShieldCheck className="w-4 h-4 text-[#ff2e93] mt-0.5 shrink-0" />
          <p className="text-xs sm:text-sm text-white/65 font-body">
            Watermarked previews only — select your favourites, add them to your cart and use credits to unlock full-quality
            downloads once the secure download system is activated.
          </p>
        </div>
      </section>

      <section className="max-w-[1280px] mx-auto px-5 sm:px-8 mt-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-3 flex-1 glass chrome-border rounded-full px-4 py-2">
            <Search className="w-4 h-4 text-white/45" />
            <input data-testid="gallery-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search photos…" className="flex-1 bg-transparent text-sm text-white placeholder-white/35 outline-none font-body" />
          </div>
          <div className="flex gap-2">
            {["all", "featured"].map((s) => (
              <button key={s} data-testid={`gallery-filter-${s}`} onClick={() => setFilter(s)} className={`text-[10px] tracking-luxe uppercase px-3 py-1.5 rounded-full border ${filter === s ? "border-transparent bg-gradient-to-r from-[#ff2e93] to-[#18d3ff] text-white" : "border-white/15 text-white/55 hover:text-white"}`}>{s}</button>
            ))}
          </div>
        </div>

        {loading ? <p className="text-sm text-white/40">Loading…</p> :
         list.length === 0 ? (
          <div className="glass chrome-border rounded-md p-10 text-center text-white/55">
            This gallery is being prepared. Photos will appear here once uploaded by WelkomBiz Photography.
          </div>
         ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
            {list.map((p) => {
              const sel = selected.has(p.id);
              const locked = p.purchased || p.in_cart;
              return (
                <div key={p.id} data-testid={`gallery-photo-${p.id}`} className={`group relative photo-frame chrome-border aspect-square overflow-hidden ${sel ? "ring-2 ring-[#ff2e93]" : ""}`}>
                  <button onClick={() => setOpen(p)} className="absolute inset-0 z-0" aria-label="Preview">
                    {p.thumbnail_url
                      ? <img src={p.thumbnail_url} alt={p.display_name} loading="lazy" className="w-full h-full object-cover" />
                      : <div className="w-full h-full bg-black/60 flex items-center justify-center text-white/30"><ImageIcon className="w-6 h-6" /></div>}
                  </button>
                  {p.is_featured && <Star className="absolute top-2 left-2 w-3.5 h-3.5 text-[#ffd166] fill-[#ffd166] z-10 pointer-events-none" />}

                  {/* Status badges */}
                  {p.purchased && (
                    <span data-testid={`gallery-badge-purchased-${p.id}`} className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 text-[9px] tracking-luxe uppercase px-2 py-0.5 rounded-full glass-strong border border-[#7fe6ff]/40 text-[#7fe6ff]">
                      <BadgeCheck className="w-3 h-3" /> Purchased
                    </span>
                  )}
                  {!p.purchased && p.in_cart && (
                    <span data-testid={`gallery-badge-in-cart-${p.id}`} className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 text-[9px] tracking-luxe uppercase px-2 py-0.5 rounded-full glass-strong border border-[#ffd479]/40 text-[#ffd479]">
                      <ShoppingBag className="w-3 h-3" /> In Cart
                    </span>
                  )}

                  {/* Selection checkbox */}
                  {!locked && (
                    <button
                      data-testid={`gallery-select-${p.id}`}
                      onClick={(e) => { e.stopPropagation(); toggleSelect(p); }}
                      className={`absolute top-2 right-2 z-10 w-7 h-7 rounded-full flex items-center justify-center border transition-all ${sel ? "bg-gradient-to-br from-[#ff2e93] to-[#18d3ff] border-transparent text-white" : "glass-strong border-white/30 text-white/70 hover:text-white"}`}
                      aria-label={sel ? "Deselect" : "Select"}
                    >
                      <Check className={`w-3.5 h-3.5 ${sel ? "" : "opacity-0 group-hover:opacity-100"}`} />
                    </button>
                  )}

                  <div className={`absolute inset-x-0 bottom-0 p-3 pointer-events-none ${sel ? "opacity-100" : "opacity-0 group-hover:opacity-100"} transition-opacity`} style={{ background: "linear-gradient(180deg, transparent, rgba(0,0,0,0.85))" }}>
                    <p className="text-[10px] tracking-luxe uppercase text-white truncate">{p.display_name}</p>
                  </div>
                </div>
              );
            })}
          </div>
         )
        }
      </section>

      {/* Floating selection bar */}
      {selected.size > 0 && (
        <div data-testid="gallery-selection-bar" className="fixed inset-x-0 bottom-0 z-40 px-4 pb-4">
          <div className="max-w-[1100px] mx-auto glass-strong chrome-border rounded-full p-2 pl-5 flex items-center gap-3 shadow-2xl">
            <div className="flex-1 min-w-0 flex items-center gap-3 overflow-hidden">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#ff2e93] to-[#18d3ff] text-white flex items-center justify-center font-display">{selected.size}</div>
              <div className="min-w-0">
                <p className="text-sm text-white truncate">{selected.size} photo{selected.size === 1 ? "" : "s"} selected</p>
                <p className="text-[10px] tracking-luxe uppercase text-white/45 truncate">Add to cart to see your total</p>
              </div>
            </div>
            <button data-testid="gallery-clear-selection" onClick={clearSelection} className="hidden sm:inline-flex btn-ghost !py-2 !px-3 !text-[10px]"><X className="w-3.5 h-3.5" /> Clear</button>
            <button data-testid="gallery-add-selected" onClick={handleAddToCart} disabled={adding} className="btn-primary !py-2.5 !px-5 !text-[11px] disabled:opacity-60">
              <ShoppingBag className="w-3.5 h-3.5" /> {adding ? "Adding…" : "Add to Cart"}
            </button>
          </div>
        </div>
      )}

      {/* Photo modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm" onClick={() => setOpen(null)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="gallery-photo-modal" className="glass-strong chrome-border rounded-md p-3 sm:p-5 max-w-5xl w-full max-h-[94vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-3">
              <div className="min-w-0">
                <p className="text-[10px] tracking-luxe uppercase text-white/45">{data.event?.title} · {data.subfolder?.name}</p>
                <h3 className="font-display text-xl sm:text-2xl text-chrome truncate">{open.display_name}</h3>
              </div>
              <button data-testid="gallery-modal-close" onClick={() => setOpen(null)} className="w-9 h-9 rounded-full chrome-border bg-black/40 text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <div className="photo-frame chrome-border bg-black/60 max-h-[68vh] flex items-center justify-center">
              {open.preview_url
                ? <img src={open.preview_url} alt={open.display_name} className="w-full h-auto max-h-[68vh] object-contain" />
                : <div className="w-full h-[40vh] flex items-center justify-center text-white/30"><ImageIcon className="w-10 h-10" /></div>}
            </div>
            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3 text-[11px] text-white/55 font-body">
                <ShieldCheck className="w-3.5 h-3.5 text-[#ff2e93]" />
                Full-quality download will be available after secure download activation.
              </div>
              <div className="flex gap-2 flex-wrap">
                {open.purchased ? (
                  <span data-testid="modal-status-purchased" className="inline-flex items-center gap-1 text-[10px] tracking-luxe uppercase px-3 py-2 rounded-full glass-strong border border-[#7fe6ff]/40 text-[#7fe6ff]">
                    <BadgeCheck className="w-3 h-3" /> Already Purchased
                  </span>
                ) : open.in_cart ? (
                  <Link to="/customer/cart" data-testid="modal-go-to-cart" className="btn-ghost !py-2.5 !px-4 !text-[10px]">
                    <ShoppingBag className="w-3 h-3" /> In Cart — View Cart
                  </Link>
                ) : (
                  <button data-testid="modal-add-to-cart" onClick={() => addOne(open)} className="btn-primary !py-2.5 !px-4 !text-[10px]">
                    <ShoppingBag className="w-3 h-3" /> Add to Cart
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
